import React, { useMemo, useRef, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/**
 * Admin-only Printable Keepsake Card tool.
 *
 * Workflow (one screen, left=form, right=live preview):
 *   1. Owner uploads a pet photo
 *   2. Picks flower preset + watercolor style
 *   3. Clicks "Paint watercolor" → AI returns pet-in-wreath on white
 *   4. Fills name, dates, personality, header phrase
 *   5. Clicks "Draft poem" → Claude returns a short 250-400 char poem she edits
 *   6. Previews the full 8x10 card on the right
 *   7. Downloads a letter-size PDF (8x10 centered on 8.5x11 cardstock) or prints
 *
 * Nothing is stored server-side for now (ephemeral generation); if she asks
 * for a drafts library later it's a thin add on top of this.
 */

const FLOWERS = [
  { value: "mixed_memorial",    label: "Mixed memorial (eucalyptus · lavender · daisies · forget-me-nots · ferns)" },
  { value: "soft_florals",      label: "Soft florals (roses · baby's breath · peonies · hydrangea)" },
  { value: "wildflower_meadow", label: "Wildflower meadow (daisies · bluebonnets · poppies · cosmos)" },
  { value: "lavender_sage",     label: "Lavender & sage (purples and silvers only)" },
];

const STYLES = [
  { value: "soft_muted",   label: "Soft & muted (airy pastels — matches the reference)" },
  { value: "vibrant_lush", label: "Vibrant & lush (deeper saturated petals)" },
];

const HEADER_PHRASES = [
  (name) => `In Loving Memory Of Our Dear ${name || "[name]"}`,
  (name) => `In Loving Memory Of ${name || "[name]"}`,
  (name) => `Forever Loved, Forever Missed — ${name || "[name]"}`,
  (name) => `Our Sweet ${name || "[name]"}, Forever In Our Hearts`,
];

// Letter: 8.5x11in @ 96dpi = 816x1056 px.
// 8x10 memorial centered: 768x960 px with 24px L/R + 48px T/B margins.
const LETTER_W = 816;
const LETTER_H = 1056;
const CARD_W = 768;
const CARD_H = 960;
const MARGIN_X = (LETTER_W - CARD_W) / 2; // 24
const MARGIN_Y = (LETTER_H - CARD_H) / 2; // 48

export default function KeepsakeCards() {
  // Pet photo
  const [petPhoto, setPetPhoto] = useState(null); // { base64, mime }
  const fileRef = useRef(null);

  // Style choices
  const [flower, setFlower] = useState(FLOWERS[0].value);
  const [style, setStyle]   = useState(STYLES[0].value);
  const [painter, setPainter] = useState("gemini");

  // Card content
  const [petName, setPetName]       = useState("");
  const [dates, setDates]           = useState(""); // free text, e.g. "10/12/2012 – 04/05/2024"
  const [personality, setPersonality] = useState("");
  const [headerIdx, setHeaderIdx]   = useState(0);
  const [poem, setPoem]             = useState("");
  const [pronoun, setPronoun]       = useState("they");

  // Generated watercolor image (bg-free pet-in-wreath on white)
  const [watercolor, setWatercolor] = useState(null); // { base64, mime }

  // Draft management
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [draftNotes, setDraftNotes] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // Loading flags
  const [paintingImage, setPaintingImage] = useState(false);
  const [draftingPoem, setDraftingPoem]   = useState(false);
  const [downloading, setDownloading]     = useState(false);

  const headerText = useMemo(() => HEADER_PHRASES[headerIdx](petName.trim()), [headerIdx, petName]);
  const canPaint   = !!petPhoto && !paintingImage;
  const canDraft   = !!petName.trim() && !draftingPoem;

  // Load drafts list on mount so the "Load" button has a count.
  React.useEffect(() => { refreshDrafts(); }, []);

  async function refreshDrafts() {
    try {
      const { data } = await apiClient.get("/admin/keepsake/drafts");
      setDrafts(data || []);
    } catch (e) { /* silent — drafts panel just stays empty */ }
  }

  const buildPayload = () => ({
    pet_name: petName.trim() || "Untitled",
    dates,
    personality,
    pronoun,
    flower_preset: flower,
    style_preset: style,
    painter,
    header_idx: headerIdx,
    poem,
    watercolor_image_base64: watercolor?.base64 || null,
    watercolor_mime: watercolor?.mime || null,
    pet_photo_base64: petPhoto?.base64 || null,
    pet_photo_mime: petPhoto?.mime || null,
    notes: draftNotes,
  });

  const saveDraft = async () => {
    if (!petName.trim()) { toast.error("Enter a pet name before saving."); return; }
    setSavingDraft(true);
    try {
      if (currentDraftId) {
        await apiClient.put(`/admin/keepsake/drafts/${currentDraftId}`, buildPayload());
        toast.success("Draft updated ✦");
      } else {
        const { data } = await apiClient.post("/admin/keepsake/drafts", buildPayload());
        setCurrentDraftId(data.id);
        toast.success("Draft saved ✦");
      }
      refreshDrafts();
    } catch (e) {
      toast.error("Could not save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const saveAsNew = async () => {
    if (!petName.trim()) { toast.error("Enter a pet name before saving."); return; }
    setSavingDraft(true);
    try {
      const { data } = await apiClient.post("/admin/keepsake/drafts", buildPayload());
      setCurrentDraftId(data.id);
      toast.success("Saved as new draft ✦");
      refreshDrafts();
    } catch (e) {
      toast.error("Could not save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const loadDraft = async (id) => {
    try {
      const { data } = await apiClient.get(`/admin/keepsake/drafts/${id}`);
      setCurrentDraftId(data.id);
      setPetName(data.pet_name || "");
      setDates(data.dates || "");
      setPersonality(data.personality || "");
      setPronoun(data.pronoun || "they");
      setFlower(data.flower_preset || "mixed_memorial");
      setStyle(data.style_preset || "soft_muted");
      setPainter(data.painter || "gemini");
      setHeaderIdx(data.header_idx || 0);
      setPoem(data.poem || "");
      setDraftNotes(data.notes || "");
      setWatercolor(data.watercolor_image_base64 ? { base64: data.watercolor_image_base64, mime: data.watercolor_mime || "image/png" } : null);
      setPetPhoto(data.pet_photo_base64 ? { base64: data.pet_photo_base64, mime: data.pet_photo_mime || "image/jpeg" } : null);
      setShowDrafts(false);
      toast.success(`Loaded "${data.pet_name}" ✦`);
    } catch (e) {
      toast.error("Could not load draft.");
    }
  };

  const deleteDraft = async (id, name) => {
    if (!confirm(`Delete the draft for "${name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/keepsake/drafts/${id}`);
      if (currentDraftId === id) setCurrentDraftId(null);
      refreshDrafts();
      toast.success("Draft deleted.");
    } catch (e) {
      toast.error("Could not delete draft.");
    }
  };

  const newBlank = () => {
    if (!confirm("Start a new blank keepsake? Any unsaved changes will be lost.")) return;
    setCurrentDraftId(null);
    setPetPhoto(null);
    setWatercolor(null);
    setPetName(""); setDates(""); setPersonality(""); setPoem("");
    setPronoun("they"); setFlower(FLOWERS[0].value); setStyle(STYLES[0].value);
    setPainter("gemini"); setHeaderIdx(0); setDraftNotes("");
  };

  const onPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Photo must be under 10 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = dataUrl.split(",")[1];
      setPetPhoto({ base64: b64, mime: file.type || "image/jpeg" });
    };
    reader.readAsDataURL(file);
  };

  const paintWatercolor = async () => {
    if (!petPhoto) {
      toast.error("Upload a pet photo first.");
      return;
    }
    setPaintingImage(true);
    try {
      const { data } = await apiClient.post("/admin/keepsake/watercolor", {
        pet_name: petName || "the pet",
        pet_photo_base64: petPhoto.base64,
        pet_photo_mime_type: petPhoto.mime,
        flower_preset: flower,
        style_preset: style,
        model: painter,
      }, { timeout: 180000 });
      setWatercolor({ base64: data.image_base64, mime: data.mime_type });
      toast.success("Watercolor painted ✦");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed. Try again.");
    } finally {
      setPaintingImage(false);
    }
  };

  const draftPoem = async () => {
    if (!petName.trim()) {
      toast.error("Enter the pet's name first.");
      return;
    }
    setDraftingPoem(true);
    try {
      const { data } = await apiClient.post("/admin/keepsake/poem", {
        pet_name: petName.trim(),
        personality: personality.trim(),
        pronoun,
        min_chars: 250,
        max_chars: 400,
      });
      setPoem(data.poem);
      toast.success("Poem drafted — edit it to taste.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poem generation failed.");
    } finally {
      setDraftingPoem(false);
    }
  };

  const printCard = () => window.print();

  const downloadPdf = async () => {
    const node = document.getElementById("keepsake-letter-sheet");
    if (!node) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "in", format: "letter" });
      pdf.addImage(dataUrl, "PNG", 0, 0, 8.5, 11);
      pdf.save(`${(petName || "memorial").toLowerCase().replace(/\s+/g, "-")}-keepsake-card.pdf`);
      toast.success("PDF saved — ready to print on 8.5×11 cardstock.");
    } catch (e) {
      toast.error("PDF export failed. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadPng = async () => {
    if (!watercolor) {
      toast.error("Paint the watercolor first.");
      return;
    }
    setDownloading(true);
    try {
      const mime = watercolor.mime || "image/png";
      const ext = mime.includes("png") ? "png" : mime.split("/")[1] || "png";
      const link = document.createElement("a");
      link.href = `data:${mime};base64,${watercolor.base64}`;
      link.download = `${(petName || "memorial").toLowerCase().replace(/\s+/g, "-")}-watercolor.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Transparent watercolor saved.");
    } catch (e) {
      toast.error("PNG export failed. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rb-cream)] py-10 px-6 md:px-10 print:p-0 print:bg-white">
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          body { background: white !important; }
          .keepsake-ui { display: none !important; }
          #keepsake-letter-sheet { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>

      <div className="keepsake-ui max-w-7xl mx-auto">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <a href="/admin" className="text-[10px] tracking-[0.25em] uppercase text-[var(--rb-text-muted)] hover:text-[var(--rb-gold)]" data-testid="keepsake-back-to-studio">← Back to Studio</a>
            <div className="tracking-celestial text-[var(--rb-gold)] mt-2">Admin · Keepsake Cards</div>
            <h1 className="font-serif-display italic text-4xl text-[var(--rb-text-heading)]">Printable 8×10 memorial</h1>
            <p className="text-sm text-[var(--rb-text-muted)] mt-1">
              For printing on 8.5×11 letter cardstock. Admin tool — not public.
              {currentDraftId && <span className="ml-2 text-[var(--rb-gold)]">· Editing saved draft</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={newBlank} className="btn-ghost !text-xs" data-testid="keepsake-new-blank-btn">+ New blank</button>
            <button onClick={() => setShowDrafts((v) => !v)} className="btn-ghost !text-xs" data-testid="keepsake-toggle-drafts-btn">
              {showDrafts ? "Hide drafts" : `My drafts (${drafts.length})`}
            </button>
          </div>
        </div>

        {/* ───────── DRAFTS PANEL ───────── */}
        {showDrafts && (
          <section className="glass p-6 rounded-sm mb-8" data-testid="keepsake-drafts-panel">
            <div className="tracking-celestial text-[var(--rb-gold)] text-[11px] mb-4">Saved drafts</div>
            {drafts.length === 0 ? (
              <p className="font-serif-display italic text-[var(--rb-text-muted)]">No drafts yet. Save your first card below.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {drafts.map((d) => (
                  <div key={d.id} className={`border p-4 bg-white/80 flex gap-3 ${currentDraftId === d.id ? "border-[var(--rb-gold)]" : "border-[var(--rb-gold-soft)]"}`} data-testid={`keepsake-draft-card-${d.id}`}>
                    {/* Thumbnail */}
                    <div className="w-20 h-24 flex-shrink-0 bg-[var(--rb-cream)] border border-[var(--rb-gold-soft)] flex items-center justify-center overflow-hidden">
                      {d.thumbnail_data_url ? (
                        <img src={d.thumbnail_data_url} alt={d.pet_name} className="w-full h-full object-contain" data-testid={`keepsake-draft-thumb-${d.id}`} />
                      ) : (
                        <span className="text-[9px] tracking-[0.15em] uppercase text-[var(--rb-text-muted)] text-center px-1">No art yet</span>
                      )}
                    </div>
                    {/* Metadata */}
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-display italic text-lg text-[var(--rb-text-heading)] truncate">{d.pet_name || "Untitled"}</div>
                      {d.dates && <div className="text-xs text-[var(--rb-text-muted)] truncate">{d.dates}</div>}
                      {d.notes && <div className="text-xs text-[var(--rb-text-muted)] italic truncate" title={d.notes}>{d.notes}</div>}
                      <div className="text-[10px] text-[var(--rb-text-muted)] mt-1">Updated {new Date(d.updated_at).toLocaleDateString()}</div>
                      <div className="mt-2 flex items-center gap-3">
                        <button onClick={() => loadDraft(d.id)} className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-gold)]" data-testid={`keepsake-draft-load-${d.id}`}>Load</button>
                        <button onClick={() => deleteDraft(d.id, d.pet_name)} className="text-[10px] tracking-[0.2em] uppercase text-red-500/70 hover:text-red-500" data-testid={`keepsake-draft-delete-${d.id}`}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <div className="grid lg:grid-cols-2 gap-10">
          {/* ───────── FORM ───────── */}
          <div className="space-y-6">
            <section className="glass p-6 rounded-sm">
              <div className="tracking-celestial text-[var(--rb-gold)] text-[11px] mb-3">① Pet photograph</div>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPhotoUpload} className="hidden" data-testid="keepsake-upload-input" />
              <button onClick={() => fileRef.current?.click()} className="btn-ghost w-full justify-center" data-testid="keepsake-upload-btn">
                {petPhoto ? "Replace photo ✦" : "Upload pet photo"}
              </button>
              {petPhoto && (
                <div className="mt-4 flex items-center gap-3">
                  <img src={`data:${petPhoto.mime};base64,${petPhoto.base64}`} alt="pet" className="w-20 h-20 rounded-full object-cover border border-[var(--rb-gold)]" />
                  <div className="text-xs text-[var(--rb-text-muted)]">Reference locked in. The AI will match {petName.trim() || "them"} exactly.</div>
                </div>
              )}
            </section>

            <section className="glass p-6 rounded-sm space-y-4">
              <div className="tracking-celestial text-[var(--rb-gold)] text-[11px]">② Flower arrangement &amp; style</div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] block mb-2">Flowers</label>
                <select value={flower} onChange={(e) => setFlower(e.target.value)} className="w-full border border-[var(--rb-gold-soft)] bg-white/80 p-3 text-sm" data-testid="keepsake-flower-select">
                  {FLOWERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] block mb-2">Watercolor style</label>
                <select value={style} onChange={(e) => setStyle(e.target.value)} className="w-full border border-[var(--rb-gold-soft)] bg-white/80 p-3 text-sm" data-testid="keepsake-style-select">
                  {STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] block mb-2">Painter</label>
                <div className="flex gap-2">
                  {["gemini", "openai"].map((m) => (
                    <button
                      key={m}
                      onClick={() => setPainter(m)}
                      className={`flex-1 px-4 py-2 text-xs tracking-[0.2em] uppercase border ${painter === m ? "bg-[var(--rb-gold)] text-white border-[var(--rb-gold)]" : "border-[var(--rb-gold-soft)] text-[var(--rb-text-muted)]"}`}
                      data-testid={`keepsake-painter-${m}`}
                    >
                      {m === "gemini" ? "Gemini · Nano Banana" : "OpenAI · GPT Image"}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={paintWatercolor} disabled={!canPaint} className="btn-gold w-full justify-center" data-testid="keepsake-paint-btn">
                {paintingImage ? "Painting…" : watercolor ? "Repaint watercolor ✦" : "Paint watercolor ✦"}
              </button>
              <p className="text-[11px] text-[var(--rb-text-muted)] italic text-center">Image generation may take up to 60 seconds.</p>
            </section>

            <section className="glass p-6 rounded-sm space-y-4">
              <div className="tracking-celestial text-[var(--rb-gold)] text-[11px]">③ Card details</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] block mb-2">Pet's name</label>
                  <input value={petName} onChange={(e) => setPetName(e.target.value)} className="w-full border border-[var(--rb-gold-soft)] bg-white/80 p-3 text-sm" placeholder="e.g. Charlie" data-testid="keepsake-name-input" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] block mb-2">Pronoun (for poem)</label>
                  <select value={pronoun} onChange={(e) => setPronoun(e.target.value)} className="w-full border border-[var(--rb-gold-soft)] bg-white/80 p-3 text-sm" data-testid="keepsake-pronoun-select">
                    <option value="she">she/her</option>
                    <option value="he">he/him</option>
                    <option value="they">they/them</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] block mb-2">Dates (birth/adoption – passing)</label>
                <input value={dates} onChange={(e) => setDates(e.target.value)} className="w-full border border-[var(--rb-gold-soft)] bg-white/80 p-3 text-sm" placeholder="10/12/2012 – 04/05/2024" data-testid="keepsake-dates-input" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] block mb-2">Personality (used to draft the poem)</label>
                <textarea value={personality} onChange={(e) => setPersonality(e.target.value)} rows={2} className="w-full border border-[var(--rb-gold-soft)] bg-white/80 p-3 text-sm" placeholder="Loyal, playful, loved tag, big joyful smile…" data-testid="keepsake-personality-input" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] block mb-2">Header phrase</label>
                <select value={headerIdx} onChange={(e) => setHeaderIdx(Number(e.target.value))} className="w-full border border-[var(--rb-gold-soft)] bg-white/80 p-3 text-sm" data-testid="keepsake-header-select">
                  {HEADER_PHRASES.map((fn, i) => <option key={i} value={i}>{fn(petName.trim() || "[name]")}</option>)}
                </select>
              </div>
            </section>

            <section className="glass p-6 rounded-sm space-y-3">
              <div className="flex items-center justify-between">
                <div className="tracking-celestial text-[var(--rb-gold)] text-[11px]">④ Mini poem (250–400 chars)</div>
                <button onClick={draftPoem} disabled={!canDraft} className="text-[11px] tracking-[0.2em] uppercase text-[var(--rb-gold)] hover:text-[var(--rb-gold-deep)]" data-testid="keepsake-draft-poem-btn">
                  {draftingPoem ? "Drafting…" : "✦ Draft with Claude"}
                </button>
              </div>
              <textarea value={poem} onChange={(e) => setPoem(e.target.value)} rows={7} className="w-full border border-[var(--rb-gold-soft)] bg-white/80 p-3 text-sm font-serif-display italic text-[15px] leading-relaxed" placeholder="You left paw prints on our hearts…" data-testid="keepsake-poem-textarea" />
              <div className="flex items-center justify-between text-[11px] text-[var(--rb-text-muted)]">
                <span>{poem.length} / 400 characters</span>
                {poem.length > 0 && poem.length < 250 && <span className="text-amber-600">Too short — may look sparse</span>}
                {poem.length > 400 && <span className="text-red-600">Over the limit — trim</span>}
              </div>
            </section>

            <section className="glass p-6 rounded-sm space-y-3">
              <div className="flex items-center justify-between">
                <div className="tracking-celestial text-[var(--rb-gold)] text-[11px]">⑤ Save &amp; print</div>
                <input
                  type="text"
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  placeholder="Private note (e.g. 'Etsy order #1234')"
                  className="text-xs border border-[var(--rb-gold-soft)] bg-white/60 p-2 w-56"
                  data-testid="keepsake-notes-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={saveDraft} disabled={savingDraft || !petName.trim()} className="btn-ghost justify-center !border-[var(--rb-gold)] !text-[var(--rb-gold)]" data-testid="keepsake-save-draft-btn">
                  {savingDraft ? "Saving…" : currentDraftId ? "⇧ Update draft" : "⇧ Save draft"}
                </button>
                <button onClick={saveAsNew} disabled={savingDraft || !petName.trim()} className="btn-ghost justify-center" data-testid="keepsake-save-as-new-btn">
                  ⇧ Save as new
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[var(--rb-gold-soft)]">
                <button onClick={downloadPdf} disabled={downloading} className="btn-gold justify-center" data-testid="keepsake-download-pdf-btn">
                  {downloading ? "Saving…" : "⇩ PDF"}
                </button>
                <button onClick={downloadPng} disabled={downloading || !watercolor} className="btn-ghost justify-center !border-[var(--rb-gold)] !text-[var(--rb-gold)]" data-testid="keepsake-download-png-btn">
                  {downloading ? "Saving…" : "⇩ Image only (PNG)"}
                </button>
                <button onClick={printCard} className="btn-ghost justify-center" data-testid="keepsake-print-btn">
                  ⎙ Print
                </button>
              </div>
            </section>
          </div>

          {/* ───────── PREVIEW (also what gets rendered to PDF/print) ───────── */}
          <div className="lg:sticky lg:top-10 self-start">
            <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Live preview</div>
            <div className="relative mx-auto" style={{ width: "100%", maxWidth: `${LETTER_W}px` }}>
              <div
                id="keepsake-letter-sheet"
                data-testid="keepsake-preview"
                style={{
                  width: `${LETTER_W}px`,
                  height: `${LETTER_H}px`,
                  background: "transparent",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
                  position: "relative",
                  transform: "scale(0.7)",
                  transformOrigin: "top left",
                  overflow: "hidden",
                }}
              >
                <KeepsakeCard
                  watercolor={watercolor}
                  petName={petName}
                  dates={dates}
                  headerText={headerText}
                  poem={poem}
                />
              </div>
              <div style={{ height: `${LETTER_H * 0.7}px` }} />
            </div>
            <p className="text-[11px] text-[var(--rb-text-muted)] text-center italic mt-4">
              Preview is scaled to 70% for viewing. The downloaded PDF prints at full 8.5×11″ letter size.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function KeepsakeCard({ watercolor, petName, dates, headerText, poem }) {
  // No background fill — the entire sheet prints transparent so the cream
  // cardstock itself shows through on the printed 8×10. Preview page keeps
  // its own cream backdrop so the admin can still judge contrast.
  return (
    <div
      style={{
        position: "absolute",
        left: `${MARGIN_X}px`,
        top: `${MARGIN_Y}px`,
        width: `${CARD_W}px`,
        height: `${CARD_H}px`,
        background: "transparent",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        color: "#2F2A24",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: "22px",
        paddingBottom: "22px",
        boxSizing: "border-box",
      }}
    >
      {/* Watercolor pet-in-wreath image — larger now (per Charlie reference) */}
      <div style={{ width: "100%", height: "540px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {watercolor ? (
          <img
            src={`data:${watercolor.mime};base64,${watercolor.base64}`}
            alt="pet watercolor"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            crossOrigin="anonymous"
          />
        ) : (
          <div style={{ textAlign: "center", color: "#9B8E78", fontStyle: "italic", fontSize: "18px" }}>
            Upload a photo and paint the watercolor
            <br/><span style={{ fontSize: "13px" }}>— the pet-in-wreath will appear here —</span>
          </div>
        )}
      </div>

      {/* Pet name */}
      <div style={{ marginTop: "6px", fontSize: "40px", fontWeight: 500, letterSpacing: "0.12em", textAlign: "center" }}>
        {(petName || "PET NAME").toUpperCase()}
      </div>

      {/* Dates */}
      {dates && (
        <div style={{ marginTop: "0px", fontSize: "20px", color: "#5A4E3B" }}>
          ({dates})
        </div>
      )}

      {/* Header phrase */}
      <div style={{ marginTop: "12px", fontSize: "22px", textAlign: "center", color: "#2F2A24" }}>
        {headerText}
      </div>

      {/* Poem */}
      <div style={{ marginTop: "10px", fontSize: "18px", lineHeight: 1.6, textAlign: "center", padding: "0 56px", color: "#3A332A", whiteSpace: "pre-wrap" }}>
        {poem || "The poem appears here once drafted."}
      </div>
    </div>
  );
}
